import React, { useCallback } from 'react'
import { Input, Form } from 'antd'
import cx from 'classnames'
import { WrappedFieldProps } from 'redux-form'
import { InputProps } from 'antd/lib/input'
import { FormItemLabelProps } from 'antd/lib/form/FormItemLabel'
import { trimStart } from 'lodash'

const { Item } = Form

type Props = WrappedFieldProps & InputProps & FormItemLabelProps & {
	search?: boolean // NOTE: only for search inputs fields -> renders diffrent styles
	customOnBlur?: (value: string | null) => any
	hideHelp?: boolean
}

const InputField = (props: Props) => {
	const {
		input,
		size,
		placeholder,
		label,
		required,
		type,
		prefix,
		disabled,
		style,
		search,
		customOnBlur,
		meta: {
			error,
			touched
		},
		hideHelp,
		maxLength,
		suffix,
		min,
		max,
		step
	} = props

	// NOTE: min/max as HTML attributes only guard the stepper arrows - the user can still type
	// an out of range value, so number inputs have to be clamped explicitly
	const clampToRange = useCallback((val: string | null) => {
		if (val === null || type !== 'number') {
			return val
		}

		const num = Number(val)
		if (Number.isNaN(num)) {
			return val
		}

		if (min !== undefined && min !== null && num < Number(min)) {
			return String(min)
		}

		if (max !== undefined && max !== null && num > Number(max)) {
			return String(max)
		}

		return val
	}, [type, min, max])

	const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		// NOTE: prevent to have "" empty string as empty value
		const val = e.target.value ? e.target.value : null
		input.onChange(clampToRange(trimStart(val as string)))
	}, [input, clampToRange])

	const onBlur = useCallback(async (e) => {
		// NOTE: prevent to have "" empty string as empty value
		const val = clampToRange(e.target.value ? e.target.value : null)

		// NOTE: wait until redux-form "BLUR" action is finished
		await input.onBlur(val)

		if (customOnBlur) {
			customOnBlur(val)
		}
	}, [input, customOnBlur, clampToRange])

	const onFocus = useCallback(async (e) => {
		// NOTE: prevent to have "" empty string as empty value
		const val = e.target.value ? e.target.value : null
		if (input.onFocus) {
			input.onFocus(val)
		}
	}, [input])

	return (
		<Item
			label={<span className={'font-semibold'}>{label}</span>}
			required={required}
			style={style}
			help={hideHelp ? undefined : (touched && error)}
			validateStatus={(error && touched) ? 'error' : undefined}
		>
			<Input
				{...input}
				className={cx({ search })}
				onChange={onChange}
				onBlur={onBlur}
				size={size || 'middle'}
				onFocus={onFocus}
				value={input.value}
				placeholder={placeholder}
				type={type || 'text'}
				prefix={prefix}
				disabled={disabled}
				maxLength={maxLength}
				suffix={suffix}
				min={min}
				max={max}
				step={step}
			/>
		</Item>
	)
}

export default React.memo(InputField)
